import type { CalendarDay } from "@/api/calendar";
import { formatUTCToLocal } from "@/utils/calendar";
import { isSameDay, isSameMonth } from "date-fns";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Clock } from "lucide-react";

import { Holiday } from "@/api/holidays";

interface CalendarGridProps {
  isLoading: boolean;
  monthCalendar: CalendarDay[];
  today: Date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEventClick: (event: any) => void;
  onSelectionComplete: () => void;
  onCellAppointment?: (date: Date | null) => void;
  onDayNumberClick?: (date: Date) => void; // Renamed prop
  holidays: Holiday[];
}

export function CalendarGrid({
  isLoading,
  monthCalendar,
  today,
  onEventClick,
  onCellAppointment,
  onDayNumberClick, // Use renamed prop
  holidays,
}: CalendarGridProps) {
  const { t, i18n } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-700 dark:text-gray-300">
          {t("loading")}
        </div>
      </div>
    );
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < monthCalendar.length; i += 7) {
    weeks.push(monthCalendar.slice(i, i + 7));
  }

  const handleCellClick = (date: Date | null) => {
    if (date && onCellAppointment && !isLoading) {
      onCellAppointment(date);
    }
  };

  const handleNavigateToDayView = (date: Date | null) => {
    if (date && onDayNumberClick && !isLoading) { // Use renamed prop
      onDayNumberClick(date);
    }
  };

  // Full weekday names for desktop, abbreviated for mobile
  const weekDays = [
    { full: t("monday"), short: t("mon_short") },
    { full: t("tuesday"), short: t("tue_short") },
    { full: t("wednesday"), short: t("wed_short") },
    { full: t("thursday"), short: t("thu_short") },
    { full: t("friday"), short: t("fri_short") },
    { full: t("saturday"), short: t("sat_short") },
    { full: t("sunday"), short: t("sun_short") },
  ];

  // Number of events to show on mobile before showing "+N more"
  const MAX_MOBILE_EVENTS = 2;

  return (
    <TooltipProvider>
      <div className="flex flex-col flex-1 h-full overflow-hidden">
        {/* Header Row */}
        <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
          {weekDays.map((day, i) => (
            <div key={i} className="p-1 md:p-2 text-center">
              <div className="text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-400">
                <span className="hidden md:inline">{day.full}</span>
                <span className="md:hidden">{day.short}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid flex-1 grid-cols-7 grid-rows-5 overflow-auto">
          {weeks.map((week, weekIndex) =>
            week.map((day, dayIndex) => {
              const dayHolidays = holidays.filter(holiday => {
                const holidayDate = new Date(holiday.date);
                return day.date && isSameDay(day.date, holidayDate);
              });

              // For mobile, limit displayed events
              const visibleEvents = day.events.slice(0, MAX_MOBILE_EVENTS);
              const hiddenEventsCount = day.events.length - MAX_MOBILE_EVENTS;

              return (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`relative border-r border-b border-gray-200 dark:border-gray-700 p-0.5 md:p-1 flex flex-col min-h-[60px] md:min-h-[100px] ${day.date && isSameMonth(day.date, today)
                    ? "bg-white dark:bg-gray-800"
                    : "bg-gray-50 dark:bg-gray-900"
                    } ${day.date && isSameDay(day.date, today)
                      ? "bg-blue-50 dark:bg-blue-900/30"
                      : ""
                    }`}
                  onClick={() => handleCellClick(day.date)}
                >
                  {day.day !== null && (
                    <div className="mb-0.5 md:mb-1 text-center">
                      <span
                        className={`text-xs md:text-sm font-medium cursor-pointer ${day.date && isSameDay(day.date, today)
                          ? "bg-blue-600 dark:bg-blue-700 text-white rounded-full w-5 h-5 md:w-6 md:h-6 inline-flex items-center justify-center"
                          : "text-gray-700 dark:text-gray-300"
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigateToDayView(day.date);
                        }}
                      >
                        {day.day}
                      </span>
                    </div>
                  )}

                  {/* Display holidays */}
                  {dayHolidays.length > 0 && (
                    <div className="mt-0.5 md:mt-1 space-y-0.5 md:space-y-1">
                      {dayHolidays.map(holiday => (
                        <Tooltip key={holiday.id} delayDuration={100}>
                          <TooltipTrigger asChild>
                            <div
                              className="px-0.5 md:px-1 py-0.5 md:py-1 text-[8px] md:text-xs truncate rounded-sm bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100"
                            >
                              {i18n.language === 'ar' ? holiday.name_ar : (i18n.language === 'fr' && holiday.name_fr ? holiday.name_fr : (holiday.name || holiday.name_en))}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div>{i18n.language === 'ar' ? holiday.name_ar : (i18n.language === 'fr' && holiday.name_fr ? holiday.name_fr : (holiday.name || holiday.name_en))}</div>
                            <div>{holiday.description}</div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  )}

                  {/* Display events - show all on desktop, limited on mobile */}
                  {day.events.length > 0 && (
                    <div
                      className={`mt-0.5 md:mt-1 space-y-0.5 md:space-y-1 ${day.events.length > 1 ? "md:max-h-20 md:overflow-y-auto md:pr-1" : ""
                        }`}
                      style={{ scrollbarWidth: "thin" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Show all events on desktop, limited on mobile */}
                      <div className="hidden md:block space-y-1">
                        {day.events.map((event) => (
                          <Tooltip key={event.id} delayDuration={100}>
                            <TooltipTrigger asChild>
                              <div
                                className={`${event.text_classes} px-1 py-1 text-xs truncate rounded-sm cursor-pointer hover:opacity-90 ${event.originalData.is_postponed ? "opacity-70" : ""
                                  }`}
                                style={event.color}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEventClick(event);
                                }}
                              >
                                {event.originalData.postponed_from && (
                                  <span className="ml-1 inline-block">
                                    <Clock className="w-3 h-3 inline" />
                                  </span>
                                )}
                                <span className={event.originalData.is_postponed ? "line-through" : ""}>
                                  {formatUTCToLocal(
                                    new Date(event.originalData.start_time * 1000),
                                    "HH:mm"
                                  )}{" "}
                                  - {event.title}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div>
                                <div>{event.title}</div>
                                <div>
                                  {formatUTCToLocal(
                                    new Date(event.originalData.start_time * 1000),
                                    "yyyy-MM-dd HH:mm"
                                  )}
                                  {event.originalData.end_time && (
                                    <> - {formatUTCToLocal(
                                      new Date(event.originalData.end_time * 1000),
                                      "HH:mm"
                                    )}</>
                                  )}
                                </div>
                                <div>
                                  {t('assigned_to')}: {event.originalData.assigned_to.name}
                                </div>
                                {event.originalData.is_postponed && (
                                  <div className="text-amber-500 dark:text-amber-400">
                                    {t("meetingPostponed")}
                                  </div>
                                )}
                                {event.originalData.postponed_from && (
                                  <div>
                                    {t("postponedFromMsg", {
                                      date: formatUTCToLocal(
                                        new Date(event.originalData.postponed_from.start_time * 1000),
                                        "yyyy-MM-dd HH:mm"
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>

                      {/* Mobile: show limited events with "+N more" */}
                      <div className="md:hidden space-y-0.5">
                        {visibleEvents.map((event) => (
                          <div
                            key={event.id}
                            className={`${event.text_classes} px-0.5 py-0.5 text-[8px] truncate rounded-sm cursor-pointer hover:opacity-90 ${event.originalData.is_postponed ? "opacity-70" : ""
                              }`}
                            style={event.color}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                          >
                            <span className={event.originalData.is_postponed ? "line-through" : ""}>
                              {formatUTCToLocal(
                                new Date(event.originalData.start_time * 1000),
                                "HH:mm"
                              )}
                            </span>
                          </div>
                        ))}
                        {hiddenEventsCount > 0 && (
                          <div
                            className="text-[8px] text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToDayView(day.date);
                            }}
                          >
                            +{hiddenEventsCount} {t("more")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
